import { createContext, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import config from "@/config";
import { BackendClient } from "@advanced-sales-technologies/backend-client";
import { useLogto } from "@logto/react";
import axios, { InternalAxiosRequestConfig } from "axios";
import { jwtDecode } from "jwt-decode";

export type ApiContextType = {
  apiClient: BackendClient;
};

export const ApiContext = createContext<ApiContextType | undefined>(undefined);

const MAX_ATTEMPTS = 30;

const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessToken, signIn, isLoading, isAuthenticated } = useLogto();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const isLoadingRef = useRef(isLoading);
  const isAuthenticatedRef = useRef(isAuthenticated);

  const apiClient = useMemo(() => {
    const clientConfig = {
      BASE: config.BACKEND_URL,
    };
    return new BackendClient(clientConfig);
  }, []);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    isAuthenticatedRef.current = isAuthenticated;
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    const requestInterceptor = async (axiosConfig: InternalAxiosRequestConfig) => {
      let attempts = 0;
      while (isLoadingRef.current && !isAuthenticatedRef.current) {
        if (attempts >= MAX_ATTEMPTS) {
          break;
        }
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      let tmpAccessToken = accessToken;
      if (!tmpAccessToken || jwtDecode(tmpAccessToken).exp! < Date.now() / 1000) {
        tmpAccessToken = (await getAccessToken(config.LOGTO_RESOURCE)) as string;
        if (!tmpAccessToken) await signIn(config.FRONTEND_URL + config.LOGTO_CALLBACK);
        setAccessToken(tmpAccessToken);
      }

      axiosConfig.headers!.Authorization = `Bearer ${tmpAccessToken}`;
      return axiosConfig;
    };

    const interceptorId = axios.interceptors.request.use(requestInterceptor);

    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [accessToken]);

  return <ApiContext.Provider value={{ apiClient }}>{children}</ApiContext.Provider>;
};

export default ApiProvider;
